import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { apiClient } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { STATUS_META } from "@/lib/status";

const STATUS_COLOR = {
  NORMAL: "#16A34A",
  RESTRICTED: "#D97706",
  LIMITED_VISITATION: "#EA580C",
  LOCKDOWN: "#DC2626",
  VISITATION_SUSPENDED: "#B91C1C",
  EMERGENCY: "#7F1D1D",
  UNKNOWN: "#64748B",
};

export default function MapPage() {
  const { t } = useI18n();
  const [facilities, setFacilities] = useState([]);

  useEffect(() => {
    apiClient.get("/facilities").then((r) => setFacilities(r.data));
  }, []);

  const withCoords = facilities.filter((f) => f.lat && f.lon);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8" data-testid="map-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">{t("map.title")}</h1>
      <p className="mt-1 text-sm text-slate-600">{t("map.subtitle")}</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_280px]">
        <Card className="overflow-hidden border-slate-200" style={{ height: 560 }}>
          <MapContainer center={[39.8283, -98.5795]} zoom={4} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
            <LayersControl position="topright">
              <LayersControl.BaseLayer checked name="Standard">
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Topographic">
                <TileLayer
                  attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
                  url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            {withCoords.map((f) => (
              <CircleMarker
                key={f.id}
                center={[f.lat, f.lon]}
                radius={9}
                pathOptions={{
                  color: STATUS_COLOR[f.status] || STATUS_COLOR.UNKNOWN,
                  fillColor: STATUS_COLOR[f.status] || STATUS_COLOR.UNKNOWN,
                  fillOpacity: 0.75,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="min-w-[200px]" data-testid={`map-popup-${f.id}`}>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{f.system}</div>
                    <div className="font-bold text-slate-900">{f.name}</div>
                    <div className="text-xs text-slate-600">{f.city}, {f.state}</div>
                    <div className="mt-2 inline-block rounded px-2 py-0.5 text-xs font-semibold text-white" style={{ background: STATUS_COLOR[f.status] }}>
                      {t(`status.${f.status}`)}
                    </div>
                    <div className="mt-2">
                      <Link to={`/facility/${f.id}`} className="text-xs font-semibold text-blue-700 hover:underline">
                        {t("results.viewFacility")} →
                      </Link>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </Card>

        <Card className="p-4 border-slate-200">
          <h2 className="font-heading font-bold text-slate-900">{t("map.legend")}</h2>
          <ul className="mt-3 space-y-2 text-sm">
            {Object.keys(STATUS_COLOR).map((s) => {
              const count = withCoords.filter((f) => f.status === s).length;
              return (
                <li key={s} className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ background: STATUS_COLOR[s] }} />
                  <span className="flex-1 text-slate-700">{t(`status.${s}`)}</span>
                  <span className="font-mono text-xs text-slate-500">{count}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-4 border-t border-slate-200 pt-3 text-xs text-slate-500">
            Total facilities on map: <span className="font-semibold text-slate-900">{withCoords.length}</span>
          </div>
        </Card>
      </div>
    </div>
  );
}
